/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import { ResponsiveTable } from "../../components/mobile/ResponsiveTable";
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "../../components/ui/sonner";
import { Loader2, Target } from "lucide-react";
import { DEPTS, DEPTS_EN, inp } from "./hrmsShared";

const GRADE_CLS = {
  A: "bg-emerald-100 text-emerald-700", B: "bg-sky-100 text-sky-700",
  C: "bg-amber-100 text-amber-700", D: "bg-red-100 text-red-600",
};
const GradeBadge = ({ g }) => <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${GRADE_CLS[g] || "bg-slate-100 text-slate-500"}`}>{g}</span>;

const KPI_EN = {
  "签约单数": "Signed deals", "签约金额": "Signed amt", "当期收款": "Collected", "在跟项目": "Active leads",
  "在管项目": "Active projects", "延期项目": "Delayed", "已完工": "Completed", "累计毛利": "Gross profit",
  "毛利率%": "Margin %", "当期完成任务": "Tasks done", "按期完成": "On-time", "待返工": "Rework pending",
};
const NOTE_EN = "Composite = perf 70% + attendance 30%; sales = signed/collections relative ranking; engineering = delay deductions + completion bonus; design = on-time rate − rework deductions; roles without direct data default to 60.";

export default function HRMSPerformance() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [fDept, setFDept] = useState("");
  const [dlg, setDlg] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    api.get("/hrms/perf/kpi", { params: { period } }).then(({ data }) => setData(data))
      .catch((e) => { toast.error(e?.detailText || "Load failed"); setData({ rows: [] }); });
  }, [api, period]);
  useEffect(() => { reload(); }, [reload]);

  if (!data) return <div className="p-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  const rows = data.rows.filter((r) => !fDept || r.department === fDept);
  const gradeCount = (g) => data.rows.filter((r) => r.finalGrade === g).length;
  const saveReview = async () => {
    setBusy(true);
    try {
      await api.post("/hrms/perf/reviews", { employeeId: dlg.employeeId, period, grade: dlg.newGrade, comment: dlg.comment || "" });
      toast.success(T(`${dlg.name} 绩效已校准为 ${dlg.newGrade}`, `${dlg.name} calibrated to ${dlg.newGrade}`)); setDlg(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || T("保存失败", "Save failed")); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="hrms-perf-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-slate-800">{T("绩效考核", "Performance Review")}</h1>
          <p className="text-xs text-slate-400 mt-0.5">{T("项目制 KPI 直采工程 / 设计 / CRM / 考勤真实数据 · 自动评分 + 经理校准（设计方案模块 10）", "KPIs pulled directly from engineering / design / CRM / attendance data · auto scoring + manager calibration")}</p>
        </div>
        <div className="flex-wrap md:flex-nowrap ml-auto flex items-center gap-2">
          <input data-testid="hrms-perf-period" type="month" className="text-sm rounded-md border border-slate-200 px-2.5 py-1.5" value={period} onChange={(e) => setPeriod(e.target.value)} />
          <select data-testid="hrms-perf-dept" className="text-sm rounded-md border border-slate-200 px-2.5 py-1.5" value={fDept} onChange={(e) => setFDept(e.target.value)}>
            <option value="">{T("全部部门", "All departments")}</option>
            {Object.keys(DEPTS).map((k) => <option key={k} value={k}>{(zh ? DEPTS : DEPTS_EN)[k]}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {["A", "B", "C", "D"].map((g) => (
          <div key={g} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`hrms-perf-grade-${g}`}>
            <p className="text-xs text-slate-500 flex items-center gap-1.5"><GradeBadge g={g} />{g === "A" ? T("优秀 ≥85", "Excellent ≥85") : g === "B" ? T("良好 ≥70", "Good ≥70") : g === "C" ? T("合格 ≥55", "Fair ≥55") : T("待改进", "Needs work")}</p>
            <p className="text-2xl font-extrabold text-slate-800 mt-1">{gradeCount(g)}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <ResponsiveTable className="w-full text-sm" data-testid="hrms-perf-table">
          <thead><tr className="border-b border-slate-100 text-left text-[11px] text-slate-400">
            <th className="px-3 py-2">{T("员工", "Employee")}</th><th className="px-3 py-2">{T("部门", "Dept")}</th><th className="px-3 py-2">{T("KPI 直采", "Live KPIs")}</th>
            <th className="px-3 py-2">{T("业绩分", "Perf")}</th><th className="px-3 py-2">{T("考勤分", "Attend")}</th><th className="px-3 py-2">{T("综合", "Composite")}</th>
            <th className="px-3 py-2">{T("自动评级", "Auto")}</th><th className="px-3 py-2">{T("校准评级", "Calibrated")}</th><th className="px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-slate-300">{T("暂无数据", "No data")}</td></tr> :
              rows.map((r) => (
                <tr key={r.employeeId} className="border-b border-slate-50" data-testid={`hrms-perf-row-${r.employeeId}`}>
                  <td className="px-3 py-2 font-semibold text-slate-700">{r.name}<p className="text-[10px] text-slate-400 font-normal">{r.role}</p></td>
                  <td className="px-3 py-2 text-xs text-slate-500">{(zh ? DEPTS : DEPTS_EN)[r.department] || r.department}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(r.kpi || {}).map(([k, v]) => (
                        <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{zh ? k : (KPI_EN[k] || k)} <b className="text-slate-700">{typeof v === "number" && v > 999 ? "$" + Math.round(v).toLocaleString() : v}</b></span>
                      ))}
                      {Object.keys(r.kpi || {}).length === 0 && <span className="text-[10px] text-slate-300">{T("数据积累中（默认 60 分）", "Accumulating (default 60)")}</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2">{r.perfScore}{!r.perfAuto && <span className="text-[9px] text-slate-300 ml-0.5">{T("默认", "default")}</span>}</td>
                  <td className="px-3 py-2">{r.attScore}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-slate-800 w-7">{r.composite}</span>
                      <div className="h-1.5 w-16 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full rounded-full ${r.composite >= 85 ? "bg-emerald-500" : r.composite >= 70 ? "bg-sky-400" : r.composite >= 55 ? "bg-amber-400" : "bg-red-400"}`} style={{ width: `${r.composite}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2"><GradeBadge g={r.grade} /></td>
                  <td className="px-3 py-2">{r.review ? <span className="inline-flex items-center gap-1"><GradeBadge g={r.review.grade} /><span className="text-[10px] text-slate-400">{r.review.by}</span></span> : <span className="text-slate-300 text-xs">-</span>}</td>
                  <td className="px-3 py-2">
                    {data.canReview && <button data-testid={`hrms-perf-review-${r.employeeId}`} onClick={() => setDlg({ ...r, newGrade: r.finalGrade, comment: r.review?.comment || "" })}
                      className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] rounded-full border border-slate-300 text-slate-500 hover:bg-slate-50 font-semibold px-3 py-1">{T("校准", "Calibrate")}</button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </ResponsiveTable>
      </div>
      <p className="text-[11px] text-slate-400 flex items-center gap-1"><Target className="h-3 w-3" />{zh ? data.note : NOTE_EN}</p>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{T("绩效校准", "Performance Calibration")} · {dlg?.name}（{period}）</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3 text-sm" data-testid="hrms-perf-review-dlg">
              <div className="rounded-lg bg-slate-50 p-3 space-y-1">
                {Object.entries(dlg.kpi || {}).map(([k, v]) => (
                  <p key={k} className="text-xs text-slate-500 flex justify-between">{zh ? k : (KPI_EN[k] || k)}<b className="text-slate-700">{typeof v === "number" && v > 999 ? "$" + Math.round(v).toLocaleString() : v}</b></p>
                ))}
                <p className="text-xs text-slate-500 flex justify-between">{T("出勤 / 迟到 / 早退", "Days / Late / Early-out")}<b className="text-slate-700">{dlg.attendance?.days} / {dlg.attendance?.late} / {dlg.attendance?.early}</b></p>
                <p className="text-xs text-slate-500 flex justify-between">{T("综合分（业绩70% + 考勤30%）", "Composite (perf 70% + attendance 30%)")}<b className="text-slate-700">{dlg.composite} · {T("自动", "auto")} {dlg.grade}</b></p>
              </div>
              <label className="block text-xs text-slate-500">{T("校准评级", "Calibrated grade")}
                <div className="flex gap-2 mt-1">
                  {["A", "B", "C", "D"].map((g) => (
                    <button key={g} data-testid={`hrms-perf-grade-btn-${g}`} onClick={() => setDlg((d) => ({ ...d, newGrade: g }))}
                      className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-sm font-bold rounded-lg px-4 py-1.5 border ${dlg.newGrade === g ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{g}</button>
                  ))}
                </div>
              </label>
              <label className="block text-xs text-slate-500">{T("校准说明", "Notes")}
                <textarea data-testid="hrms-perf-comment" rows={3} className={inp} value={dlg.comment} onChange={(e) => setDlg((d) => ({ ...d, comment: e.target.value }))} placeholder={T("校准原因 / 360 反馈要点…", "Calibration reason / 360 feedback highlights…")} />
              </label>
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                <button onClick={() => setDlg(null)} className="text-xs rounded-full border border-slate-300 text-slate-500 font-semibold px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-perf-review-save" disabled={busy} onClick={saveReview} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("保存校准", "Save")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
